import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import ChevronLeftIcon from "@redotech/redo-icons/chevron-left_filled.svg";
import ChevronRightIcon from "@redotech/redo-icons/chevron-right_filled.svg";
import LayoutLeftSvg from "@redotech/redo-icons/layout-left.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoSingleSelectDropdownInputButton } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useMemo, useRef, useState } from "react";
import { useStatusFilter } from "../filters/filters-context";
import {
  STATUS_OPTIONS,
  type StatusValue,
} from "../filters/support-tickets-filter-toolbar";
import { useSupportTicketsTableContext } from "../table/support-tickets-table-context";
import {
  SupportTicketCard,
  SupportTicketCardEmpty,
  SupportTicketCardSkeleton,
} from "./support-ticket-card";

export const SupportTicketCardList = memo(function SupportTicketCardList({
  onCollapse,
}: {
  onCollapse: () => void;
}) {
  const {
    data: supportTickets,
    isLoading,
    currentPage,
    pageSize,
    totalCount,
    hasPreviousPage,
    hasNextPage,
    goToNextPage,
    goToPreviousPage,
    selectedSupportTicketId,
    openSupportTicket,
  } = useSupportTicketsTableContext();

  const { statuses, setStatuses } = useStatusFilter();

  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [statusDropdownButtonRef, setStatusDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);

  const hasScrolledRef = useRef(false);
  const selectedCardRef = useCallback((node: HTMLDivElement | null) => {
    if (node && !hasScrolledRef.current) {
      hasScrolledRef.current = true;
      node.scrollIntoView({ block: "center" });
    }
  }, []);

  const isAllSelected = statuses.length === 0;

  const selectAllState = useMemo(() => {
    if (isAllSelected) {
      return true as const;
    }
    if (statuses.length < STATUS_OPTIONS.length) {
      return "indeterminate" as const;
    }
    return true as const;
  }, [isAllSelected, statuses.length]);

  const selectedStatusItems = useMemo<RedoListItem<StatusValue>[]>(
    () =>
      statuses
        .map((s) => STATUS_OPTIONS.find((o) => o.id === s))
        .filter((o): o is RedoListItem<StatusValue> => o !== undefined),
    [statuses],
  );

  const statusTriggerLabel = useMemo(() => {
    if (isAllSelected || statuses.length === STATUS_OPTIONS.length) {
      return "All";
    }
    if (statuses.length === 1) {
      const option = STATUS_OPTIONS.find((o) => o.id === statuses[0]);
      return (
        (option?.type === "text" ? option.text : null) ?? statuses[0] ?? "All"
      );
    }
    return `${statuses.length} statuses`;
  }, [isAllSelected, statuses]);

  const statusTriggerItem = useMemo<RedoListItem<string>>(
    () => ({
      id: "status-trigger",
      value: statusTriggerLabel,
      type: "text",
      text: statusTriggerLabel,
    }),
    [statusTriggerLabel],
  );

  const handleSelectAllToggled = useCallback(() => {
    setStatuses([]);
  }, [setStatuses]);

  const handleStatusItemsSelected = useCallback(
    (items: RedoListItem<StatusValue>[]) => {
      if (items.length >= STATUS_OPTIONS.length) {
        setStatuses([]);
      } else {
        setStatuses(items.map((i) => i.value));
      }
    },
    [setStatuses],
  );

  const handleCardClick = useCallback(
    (supportTicket: SupportTicketListItemResponse) => {
      openSupportTicket(supportTicket.id);
    },
    [openSupportTicket],
  );

  const startIndex = currentPage * pageSize + 1;
  const endIndex = Math.min(startIndex + supportTickets.length - 1, totalCount);

  return (
    <Flex bgColor="primary" dir="column" gap="none" height="full">
      <Flex
        align="center"
        borderBottomWidth="1px"
        borderColor="secondary"
        borderStyle="solid"
        gap="sm"
        pl="md"
        pr="md"
        shrink={0}
        style={{ height: 48 }}
      >
        <Flex>
          <RedoSingleSelectDropdownInputButton
            isDropdownOpen={statusDropdownOpen}
            onClick={() => setStatusDropdownOpen((prev) => !prev)}
            ref={setStatusDropdownButtonRef}
            selectedItem={statusTriggerItem}
            size="xs"
          />
          <RedoMultiselectDropdown<StatusValue>
            dropdownAnchor={statusDropdownButtonRef}
            dropdownOpen={statusDropdownOpen}
            fitToAnchor={false}
            items={STATUS_OPTIONS}
            selectAllLabel="All"
            selectAllState={selectAllState}
            selectAllToggled={handleSelectAllToggled}
            selectedItems={selectedStatusItems}
            setDropdownOpen={setStatusDropdownOpen}
            setSelectedItems={handleStatusItemsSelected}
            size="xs"
          />
        </Flex>
        <Flex align="center" gap="xs">
          <RedoButton
            disabled={!hasPreviousPage}
            hierarchy="tertiary"
            IconLeading={ChevronLeftIcon}
            onClick={goToPreviousPage}
            size="xs"
          />
          <Text fontSize="xs" textColor="secondary">
            {totalCount > 0
              ? `${startIndex}-${endIndex} of ${totalCount}`
              : "0"}
          </Text>
          <RedoButton
            disabled={!hasNextPage}
            hierarchy="tertiary"
            IconLeading={ChevronRightIcon}
            onClick={goToNextPage}
            size="xs"
          />
        </Flex>
        <Flex align="center" flex={1} justify="flex-end">
          <RedoButton
            hierarchy="tertiary"
            IconLeading={LayoutLeftSvg}
            onClick={onCollapse}
            size="sm"
          />
        </Flex>
      </Flex>

      <Flex
        dir="column"
        flex={1}
        gap="none"
        overflowY="auto"
        style={{ minHeight: 0 }}
      >
        {isLoading ? (
          <SupportTicketCardSkeleton count={5} />
        ) : supportTickets.length === 0 ? (
          <SupportTicketCardEmpty />
        ) : (
          supportTickets.map((supportTicket) => {
            const selected = supportTicket.id === selectedSupportTicketId;
            return (
              <div
                key={supportTicket.id}
                ref={selected ? selectedCardRef : undefined}
              >
                <SupportTicketCard
                  isActive={selected}
                  onClick={() => handleCardClick(supportTicket)}
                  supportTicket={supportTicket}
                />
              </div>
            );
          })
        )}
      </Flex>
    </Flex>
  );
});
